/**
 * EquipmentStepper Component
 *
 * This component provides a guided, step-by-step interface for adding new equipment setups.
 * It manages the process of adding equipment models, equipment, and their attributes in sequence.
 *
 * Features:
 * - Multi-step form with progress tracking
 * - Model creation and selection
 * - Equipment addition with multiple instances
 * - Attribute assignment
 * - Step navigation and completion tracking
 * - Equipment list management
 *
 * Data Flow:
 * 1. Step 0: Add Equipment Model
 * 2. Step 1: Add Equipment (can add multiple)
 * 3. Step 2: Add Equipment Attributes
 *
 * State Management:
 * - activeStep: Current step in the process
 * - completed: Tracks completed steps
 * - skipped: Tracks skipped steps
 * - modelId: ID of the created/selected model
 * - addedEquipments: List of added equipment items
 *
 * Props:
 * @param {Function} onClose - Callback when the process is completed
 */

import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  StepButton,
  Card,
  CardContent,
  IconButton,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddEquipment from "./AddEquipment";
import AddEquipmentModel from "./AddEquipmentModel";
import AddEquipmentAttribute from "./AddEquipmentAttribute";

// Define the steps in the equipment setup process
const steps = [
  "Add Equipment Model",
  "Add Equipment",
  "Add Equipment Attributes",
];

const EquipmentStepper = ({ onClose }) => {
  // State management for the stepper
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [skipped, setSkipped] = useState(new Set());
  const [modelId, setModelId] = useState(null);
  const [addedEquipments, setAddedEquipments] = useState([]);
  const [showEquipmentForm, setShowEquipmentForm] = useState(true);

  /**
   * Check if a step has been skipped
   * @param {number} step - Step index to check
   * @returns {boolean} True if step was skipped
   */
  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  /**
   * Move to the next step
   * Handles skipped steps and equipment form visibility
   */
  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
    if (activeStep === 1) {
      setShowEquipmentForm(false);
    }
  };

  /**
   * Move to the previous step
   */
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  /**
   * Skip the current step
   */
  const handleSkip = () => {
    setSkipped((prev) => new Set(prev.add(activeStep)));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  /**
   * Navigate to a specific step
   * @param {number} step - Step index to navigate to
   */
  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  /**
   * Mark the current step as completed
   * Moves to the next step if not the last step
   */
  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    if (activeStep !== steps.length - 1) {
      handleNext();
    }
  };

  /**
   * Handle successful model creation
   * @param {string} modelId - ID of the created model
   */
  const handleModelSuccess = (modelId) => {
    setModelId(modelId);
    handleComplete();
  };

  /**
   * Handle successful equipment addition
   * @param {Object} equipmentData - Data of the added equipment
   */
  const handleEquipmentSuccess = (equipmentData) => {
    setAddedEquipments((prev) => [...prev, equipmentData]);
    setShowEquipmentForm(false);
  };

  /**
   * Show the equipment form for adding another equipment
   */
  const handleAddAnotherEquipment = () => {
    setShowEquipmentForm(true);
  };

  /**
   * Remove an equipment from the list
   * @param {number} index - Index of the equipment to remove
   */
  const handleRemoveEquipment = (index) => {
    setAddedEquipments((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle successful attribute addition
   * Marks the step as completed and closes the stepper
   */
  const handleAttributeSuccess = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    setTimeout(onClose, 2000);
  };

  /**
   * Render the list of added equipment
   * @returns {JSX.Element} Equipment list component
   */
  const renderAddedEquipments = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Added Equipment
      </Typography>
      <Grid container spacing={2}>
        {addedEquipments.map((equipment, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {equipment.name}
                </Typography>
                <IconButton
                  onClick={() => handleRemoveEquipment(index)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Type: {equipment.type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Serial: {equipment.serial_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {equipment.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {equipment.model_name}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setShowEquipmentForm(true)}
        sx={{ mt: 2 }}
      >
        Add Another Equipment
      </Button>
    </Box>
  );

  /**
   * Render the content for the current step
   * @param {number} step - Current step index
   * @returns {JSX.Element} Step content component
   */
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AddEquipmentModel onClose={handleModelSuccess} onSkip={handleSkip} />
        );
      case 1:
        return (
          <Box>
            {showEquipmentForm ? (
              <AddEquipment
                onClose={handleEquipmentSuccess}
                modelId={modelId}
                onSkip={handleSkip}
              />
            ) : null}
            {addedEquipments.length > 0 && renderAddedEquipments()}
          </Box>
        );
      case 2:
        return (
          <Box>
            {addedEquipments.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Select Equipment to Add Attributes
                </Typography>
                <Grid container spacing={2}>
                  {addedEquipments.map((equipment, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          bgcolor: "background.default",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {equipment.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Type: {equipment.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Serial: {equipment.serial_number}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            <AddEquipmentAttribute
              onClose={handleAttributeSuccess}
              modelId={modelId}
              equipments={addedEquipments}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Add New Equipment Setup
        </Typography>

        <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }

            return (
              <Step key={label} {...stepProps}>
                <StepButton
                  onClick={handleStep(index)}
                  completed={completed[index]}
                  {...labelProps}
                >
                  {label}
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        <Box sx={{ mb: 2 }}>{renderStepContent(activeStep)}</Box>

        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {activeStep !== steps.length - 1 && (
            <Button onClick={handleSkip} sx={{ mr: 1 }}>
              Skip this step
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button onClick={handleComplete}>Finish</Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={activeStep === 1 && addedEquipments.length === 0}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default EquipmentStepper;
